import { DateTime } from 'luxon';

/**
 * The deployment's calendar — `CountryConfig.timeZone` resolved, plus the
 * handful of day/clock helpers every zoned call site needs.
 *
 * ## Two fallbacks, on purpose
 *
 * Before `timeZone` existed the code read the calendar two different ways, and
 * UZ and KZ must keep both exactly:
 *
 * - {@link countryTimeZone}: sites that HARDCODED `Asia/Tashkent` (the LLM
 *   spend-fuse day, the winner-contact cap, the cron slots, the «данные на …»
 *   stamp). Absent `timeZone` → `Asia/Tashkent`.
 * - {@link displayTimeZone}: sites that read a DATE in UTC (the web's deadline
 *   and «DD.MM.YYYY» dates). Absent `timeZone` → `utc`.
 *
 * Spain sets `Europe/Madrid` and both resolve to it.
 *
 * ## Why the legacy zone keeps its fixed-offset arithmetic
 *
 * Tashkent is UTC+5 all year (no DST since 1992). The helpers below answer the
 * legacy zone with the same `+5 h` arithmetic the call sites carried inline,
 * not through ICU: the UZ/KZ results are then byte-identical by construction,
 * whatever tz database the Node or browser runtime ships. Any other zone goes
 * through Luxon, which follows DST.
 */

/** The zone UZ and KZ hardcoded before `timeZone` existed: UTC+5, no DST. */
export const LEGACY_TIME_ZONE = 'Asia/Tashkent';
const LEGACY_OFFSET_MS = 5 * 3_600_000;
const DAY_MS = 86_400_000;

interface HasTimeZone {
  readonly timeZone?: string;
}

/**
 * The OWNER's clock, on EVERY deployment: what the admin panel, the CRM topic
 * and the MRR midnight snapshot count days in and print times on. The owner
 * reads all three from Tashkent, Spain's included (owner decision 2026-10-20) —
 * so these sites never follow `timeZone`, which is the CUSTOMERS' calendar.
 */
export const OWNER_TIME_ZONE = LEGACY_TIME_ZONE;

/** Is `zone` an IANA zone this runtime knows? (`Europe/Madrid` yes, `Europe/Madird` no.) */
export function isKnownTimeZone(zone: string): boolean {
  return zone.length > 0 && DateTime.now().setZone(zone).isValid;
}

/** The zone for a site that used to hardcode `Asia/Tashkent`. */
export function countryTimeZone(cfg: HasTimeZone): string {
  return cfg.timeZone ?? LEGACY_TIME_ZONE;
}

/** The zone for a site that used to read dates in UTC (deadline dates on the web). */
export function displayTimeZone(cfg: HasTimeZone): string {
  return cfg.timeZone ?? 'utc';
}

function utcDay(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/**
 * The zone's UTC offset AT the instant `ms`, in ms (Madrid: 2 h in summer, 1 h
 * in winter; the legacy zone: always 5 h). `new Date(ms + zoneOffsetMs(ms, z))`
 * read through its UTC getters is the wall clock — the idiom the call sites
 * used with a fixed `TASHKENT_OFFSET_MS`, now right across a DST change too.
 */
export function zoneOffsetMs(ms: number, zone: string): number {
  if (zone === LEGACY_TIME_ZONE) return LEGACY_OFFSET_MS;
  return DateTime.fromMillis(ms, { zone }).offset * 60_000;
}

/** `YYYY-MM-DD` — the calendar day the instant `ms` falls on in `zone`. */
export function zonedDay(ms: number, zone: string): string {
  if (zone === LEGACY_TIME_ZONE) return utcDay(ms + LEGACY_OFFSET_MS);
  return DateTime.fromMillis(ms, { zone }).toISODate() ?? utcDay(ms);
}

/** The instant (epoch ms) at which the calendar day `day` (`YYYY-MM-DD`) begins in `zone`. */
export function zonedDayStart(day: string, zone: string): number {
  if (zone === LEGACY_TIME_ZONE) return Date.parse(`${day}T00:00:00Z`) - LEGACY_OFFSET_MS;
  return DateTime.fromISO(day, { zone }).startOf('day').toMillis();
}

/** `YYYY-MM-DD` shifted by `n` calendar days (zone-free string arithmetic). */
export function addDays(day: string, n: number): string {
  return utcDay(Date.parse(`${day}T00:00:00Z`) + n * DAY_MS);
}

/** The instant (epoch ms) the day AFTER the one `ms` falls on begins in `zone`. */
export function nextZonedMidnight(ms: number, zone: string): number {
  return zonedDayStart(addDays(zonedDay(ms, zone), 1), zone);
}

/**
 * The wall clock of `ms` in `zone` — `month` 1–12, `weekday` 1 (Monday) – 7.
 * Plain numbers, so a caller can format without `Intl` (Chrome ships no `uz`
 * CLDR data; see `apps/web/src/lib/format.ts`).
 */
export function zonedParts(
  ms: number,
  zone: string,
): {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  weekday: number;
} {
  if (zone === LEGACY_TIME_ZONE) {
    const d = new Date(ms + LEGACY_OFFSET_MS);
    return {
      year: d.getUTCFullYear(),
      month: d.getUTCMonth() + 1,
      day: d.getUTCDate(),
      hour: d.getUTCHours(),
      minute: d.getUTCMinutes(),
      weekday: ((d.getUTCDay() + 6) % 7) + 1,
    };
  }
  const dt = DateTime.fromMillis(ms, { zone });
  return {
    year: dt.year,
    month: dt.month,
    day: dt.day,
    hour: dt.hour,
    minute: dt.minute,
    weekday: dt.weekday,
  };
}
